import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import {
  excluirAgendamento,
  getAgendamento,
  getMembro,
  mudarStatus,
  remarcar,
} from "../queries";
import { db } from "../../schema.dexie";
import {
  TRANSICOES,
  corMembro,
  dataLegivel,
  horaFim,
} from "../domain";
import {
  MODALIDADE_LABEL,
  TIPO_LABEL,
  type StatusAgendamento,
} from "../types";
import { StatusAgendaBadge } from "../ui/StatusAgendaBadge";

const VERBO: Record<StatusAgendamento, string> = {
  agendado: "reabrir",
  confirmado: "confirmar",
  realizado: "marcar realizada",
  faltou: "marcar falta",
  remarcado: "remarcar",
  cancelado: "cancelar",
};

export function DetalheAgendamento() {
  const nav = useNavigate();
  const { id } = useParams();
  const ag = useLiveQuery(() => (id ? getAgendamento(id) : undefined), [id]);
  const membro = useLiveQuery(
    () => (ag?.profissional_id ? getMembro(ag.profissional_id) : undefined),
    [ag?.profissional_id],
  );
  const local = useLiveQuery(
    () => (ag?.local_id ? db.local_clinica.get(ag.local_id) : undefined),
    [ag?.local_id],
  );

  const [remarcando, setRemarcando] = useState(false);
  const [novaData, setNovaData] = useState("");
  const [novaHora, setNovaHora] = useState("");

  if (!id) return null;
  if (ag === undefined) return <main className="tela"><p className="meta">carregando…</p></main>;
  if (ag === null) {
    return (
      <main className="tela">
        <button className="btn btn-fantasma" onClick={() => nav("/agenda")}>← agenda</button>
        <div className="vazio">consulta não encontrada.</div>
      </main>
    );
  }

  const acoes = TRANSICOES[ag.status];
  const cor = corMembro(membro?.cor ?? null, membro?.especialidade ?? null);

  async function aplicar(status: StatusAgendamento) {
    if (status === "remarcado") {
      setNovaData(ag!.data);
      setNovaHora(ag!.hora_inicio);
      setRemarcando(true);
      return;
    }
    await mudarStatus(ag!.id, status);
  }

  async function confirmarRemarcacao() {
    if (!novaData || !novaHora) return;
    const novo = await remarcar(ag!.id, novaData, novaHora);
    setRemarcando(false);
    if (novo) nav(`/agenda/${novo}`);
  }

  async function excluir() {
    if (!confirm("excluir esta consulta da agenda?")) return;
    await excluirAgendamento(ag!.id);
    nav("/agenda");
  }

  return (
    <main className="tela">
      <button className="btn btn-fantasma" onClick={() => nav("/agenda")}>← agenda</button>

      <div className="row-entre" style={{ alignItems: "flex-start" }}>
        <h1 className="tela-titulo" style={{ marginBottom: 4 }}>{ag.paciente_nome}</h1>
        <StatusAgendaBadge status={ag.status} />
      </div>
      <p className="tela-sub" style={{ marginTop: 0 }}>
        {dataLegivel(ag.data)} · {ag.hora_inicio}–{horaFim(ag.hora_inicio, ag.duracao_min)}
      </p>

      <div className="card secao">
        <div className="row" style={{ gap: "var(--s2)", marginBottom: "var(--s3)" }}>
          <span className="ag-prof-dot" style={{ background: cor }} />
          <strong>{membro?.nome ?? "profissional"}</strong>
          {membro?.registro_classe && (
            <span className="meta" style={{ color: "var(--text-muted)" }}>· {membro.registro_classe}</span>
          )}
        </div>
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "var(--s2)" }}>
          <li className="row-entre"><span className="meta">tipo</span><span>{TIPO_LABEL[ag.tipo]}</span></li>
          <li className="row-entre"><span className="meta">modalidade</span><span>{MODALIDADE_LABEL[ag.modalidade]}</span></li>
          {ag.modalidade === "online" && ag.link_online && (
            <li className="row-entre">
              <span className="meta">link</span>
              <a href={ag.link_online} target="_blank" rel="noopener" style={{ color: "var(--accent-forte)" }}>abrir</a>
            </li>
          )}
          {ag.modalidade !== "online" && local && (
            <li className="row-entre"><span className="meta">consultório</span><span>{local.nome}</span></li>
          )}
          {ag.paciente_contato && (
            <li className="row-entre"><span className="meta">contato</span><span>{ag.paciente_contato}</span></li>
          )}
        </ul>
        {ag.observacao_agenda && (
          <p className="rel-obs" style={{ fontStyle: "normal" }}>📌 {ag.observacao_agenda}</p>
        )}
      </div>

      {/* ações de status (transições válidas a partir do status atual) */}
      {acoes.length > 0 && !remarcando && (
        <div className="secao">
          <div className="secao-titulo">atualizar situação</div>
          <div className="status-opcoes">
            {acoes.map((s) => (
              <button key={s} className="status-opt" onClick={() => aplicar(s)}>
                {VERBO[s]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* remarcação inline: cria um novo agendamento e fecha o atual como remarcado */}
      {remarcando && (
        <div className="card secao">
          <div className="secao-titulo">remarcar para</div>
          <div className="linha-campos">
            <div className="campo" style={{ marginBottom: 0 }}>
              <label>nova data</label>
              <input type="date" value={novaData} onChange={(e) => setNovaData(e.target.value)} />
            </div>
            <div className="campo" style={{ marginBottom: 0 }}>
              <label>nova hora</label>
              <input type="time" value={novaHora} onChange={(e) => setNovaHora(e.target.value)} />
            </div>
          </div>
          <div className="row" style={{ gap: "var(--s2)", marginTop: "var(--s4)" }}>
            <button className="btn crescer" onClick={() => setRemarcando(false)}>cancelar</button>
            <button className="btn btn-primario crescer" disabled={!novaData || !novaHora} onClick={confirmarRemarcacao}>
              remarcar
            </button>
          </div>
        </div>
      )}

      <div className="row" style={{ gap: "var(--s2)", marginTop: "var(--s5)" }}>
        <Link to={`/agenda/novo/${ag.id}`} className="btn crescer">✏️ editar</Link>
        <button className="btn crescer" style={{ color: "var(--text-muted)" }} onClick={excluir}>excluir</button>
      </div>
    </main>
  );
}
